<script setup lang="ts">
import ServicePanelHeader from '@/components/panels/ServicesPanelHeader.vue'
import type { ICommonPanel } from '@/interface/commonElements/ICommonPanel'
import SliderComponent from '@/components/panels/SliderComponent.vue'
import WatermarkIcon from '@/components/media/WatermarkIcon.vue'
import ImgComponent from '@/components/media/ImgComponent.vue'
import HtmlFormatter from '@/components/HtmlFormatter.vue'
import type { PropType } from 'vue'
import { ref } from 'vue'

const props = defineProps({
  panelContent: {
    type: Array as PropType<ICommonPanel[]>,
    required: true,
  },
})
const activeTabPanel = ref(props.panelContent[0])
const changeTabPanel = (panel: ICommonPanel) => {
  activeTabPanel.value = panel
  activeMoreInformation.value = false
}
const activeMoreInformation = ref(false)
const getMoreInformation = () => {
  activeMoreInformation.value = !activeMoreInformation.value
}
</script>

<template>
  <div class="app-container -z-10">
    <ServicePanelHeader
      :panel-header="panelContent"
      @change-tab="changeTabPanel"
    />
    <div id="body-service-panel">
      <ElCard class="rounded--1">
        <div class="min-h-[60px]">
          <WatermarkIcon class="absolute -ml-5 -mt-9" :size="107" />
          <p
            class="text-sm sm:text-base font-bold color-title-light xs:ml-10 sm:ml-24 mt-4"
          >
            {{ activeTabPanel.title }}
          </p>
        </div>
        <div
          class="color-grey-border border-solid border my-8 hidden hide-section"
        ></div>
        <div
          id="body-content"
          :class="`flex flex-col ${activeMoreInformation ? '' : 'hidden'}`"
        >
          <div
            :class="`color-grey-border border-solid border my-8 md:hidden ${
              activeMoreInformation ? '' : 'hidden'
            }`"
          ></div>
          <ImgComponent
            v-if="activeTabPanel.image"
            :src="activeTabPanel.image"
            class="body-photo rounded-2xl"
            alt="body photo"
          />
          <div>
            <HtmlFormatter
              class="mb-6 content"
              :html-input="activeTabPanel.headerContent"
            ></HtmlFormatter>
            <HideableText>
              <template #content>
                <HtmlFormatter
                  class="max-lg:mt-6 content"
                  :html-input="activeTabPanel.bodyContent"
                ></HtmlFormatter>
              </template>
            </HideableText>
          </div>
        </div>
        <ClientOnly>
          <div :class="['hide-section', { active: activeMoreInformation }]">
            <div class="color-grey-border border-solid border my-8" />
            <SliderComponent
              :footer="activeTabPanel.footerPanelContent"
              :key="activeTabPanel.id"
            />
          </div>
        </ClientOnly>
        <button
          class="flex w-full mx-auto mt-6 md:hidden"
          @click="getMoreInformation"
          aria-label="Show more or less content"
        >
          <v-icon
            :name="activeMoreInformation ? 'hi-chevron-up' : 'hi-chevron-down'"
            class="color-title-dark icon w-full"
            size="2x"
          />
        </button>
      </ElCard>
    </div>
  </div>
</template>
<style scoped>
.icon {
  animation: bounce 1s infinite;
}

.active {
  display: block !important;
}

.hide-section {
  display: none;
}

#body-panel {
  transition: all 0.2s ease;
}

@media screen and (min-width: 768px) {
  #body-content {
    display: flex;
  }

  .hide-section {
    display: block;
  }
}

@media screen and (min-width: 1024px) {
  #body-content {
    display: flex;
    flex-direction: row-reverse;
  }

  .body-photo {
    width: 36vw;
    margin-left: 1rem;
    aspect-ratio: 4/3 !important;
    height: fit-content;
    height: -moz-fit-content;
  }
}

@media screen and (min-width: 1500px) {
  .body-photo {
    width: 30vw;
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(-25%);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }

  50% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
}
</style>
